/**
 * Relationship Builder
 *
 * Component for creating and managing foreign key relationships between tables.
 */

import React, { useState } from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Card from "@mui/joy/Card";
import Typography from "@mui/joy/Typography";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import IconButton from "@mui/joy/IconButton";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Tooltip from "@mui/joy/Tooltip";
import Chip from "@mui/joy/Chip";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import type {
  SchemaBuilderTable,
  SchemaBuilderRelationship,
  OnDeleteBehavior,
} from "../../types";

interface RelationshipBuilderProps {
  currentTable: SchemaBuilderTable;
  allTables: SchemaBuilderTable[];
  relationships: SchemaBuilderRelationship[];
  onAddRelationship: (
    sourceTableId: string,
    sourceColumnId: string,
    targetTableId: string,
    targetColumnId: string,
    onDelete?: OnDeleteBehavior,
  ) => void;
  onDeleteRelationship: (relationshipId: string) => void;
}

export function RelationshipBuilder({
  currentTable,
  allTables,
  relationships,
  onAddRelationship,
  onDeleteRelationship,
}: RelationshipBuilderProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [newRelation, setNewRelation] = useState({
    sourceColumnId: "",
    targetTableId: "",
    targetColumnId: "",
    onDelete: "RESTRICT" as OnDeleteBehavior,
  });

  // Other tables that can be linked to
  const otherTables = allTables.filter((t) => t.tempId !== currentTable.tempId);

  // Get non-primary-key columns that can be used as foreign keys
  const availableColumns = currentTable.columns.filter((c) => !c.isPrimaryKey);

  // Get target table columns (typically just the ID column)
  const targetTable = allTables.find(
    (t) => t.tempId === newRelation.targetTableId,
  );
  const targetColumns = targetTable?.columns || [];

  const handleAddClick = () => {
    setIsAdding(true);
    setNewRelation({
      sourceColumnId: availableColumns[0]?.tempId || "",
      targetTableId: otherTables[0]?.tempId || "",
      targetColumnId: "",
      onDelete: "RESTRICT",
    });
  };

  const handleTargetTableChange = (tableId: string) => {
    const table = allTables.find((t) => t.tempId === tableId);
    const pkColumn = table?.columns.find((c) => c.isPrimaryKey);
    setNewRelation({
      ...newRelation,
      targetTableId: tableId,
      targetColumnId: pkColumn?.tempId || "",
    });
  };

  const handleSave = () => {
    if (
      newRelation.sourceColumnId &&
      newRelation.targetTableId &&
      newRelation.targetColumnId
    ) {
      onAddRelationship(
        currentTable.tempId,
        newRelation.sourceColumnId,
        newRelation.targetTableId,
        newRelation.targetColumnId,
        newRelation.onDelete,
      );
      setIsAdding(false);
      setNewRelation({
        sourceColumnId: "",
        targetTableId: "",
        targetColumnId: "",
        onDelete: "RESTRICT",
      });
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setNewRelation({
      sourceColumnId: "",
      targetTableId: "",
      targetColumnId: "",
      onDelete: "RESTRICT",
    });
  };

  // Get display info for a relationship
  const getRelationshipInfo = (rel: SchemaBuilderRelationship) => {
    const sourceTable = allTables.find((t) => t.tempId === rel.sourceTableId);
    const targetTable = allTables.find((t) => t.tempId === rel.targetTableId);
    const sourceColumn = sourceTable?.columns.find(
      (c) => c.tempId === rel.sourceColumnId,
    );
    const targetColumn = targetTable?.columns.find(
      (c) => c.tempId === rel.targetColumnId,
    );

    const onDeleteLabel: Record<string, string> = {
      RESTRICT: "Restrict",
      CASCADE: "Cascade",
      SET_NULL: "Set Null",
    };

    return {
      sourceTable: sourceTable?.displayName || "Unknown",
      sourceColumn: sourceColumn?.displayName || "Unknown",
      targetTable: targetTable?.displayName || "Unknown",
      targetColumn: targetColumn?.displayName || "Unknown",
      isOutgoing: rel.sourceTableId === currentTable.tempId,
      onDeleteDisplay:
        onDeleteLabel[rel.onDelete] || rel.onDelete || "Restrict",
    };
  };

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography level="title-md" startDecorator={<LinkRoundedIcon />}>
          Relationships
        </Typography>
        {!isAdding && otherTables.length > 0 && availableColumns.length > 0 && (
          <Button
            size="sm"
            variant="soft"
            startDecorator={<AddRoundedIcon />}
            onClick={handleAddClick}
          >
            Add Relationship
          </Button>
        )}
      </Box>

      <Typography level="body-sm" sx={{ mb: 2, color: "text.secondary" }}>
        Connect this table to others. For example, link Deals to Companies so
        each deal knows which company it belongs to.
      </Typography>

      {/* No tables message */}
      {otherTables.length === 0 && (
        <Box
          sx={{
            p: 2,
            borderRadius: "sm",
            bgcolor: "background.level1",
            textAlign: "center",
          }}
        >
          <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
            Create another table first to connect them.
          </Typography>
        </Box>
      )}

      {/* No available columns message */}
      {otherTables.length > 0 && availableColumns.length === 0 && (
        <Box
          sx={{
            p: 2,
            borderRadius: "sm",
            bgcolor: "background.level1",
            textAlign: "center",
          }}
        >
          <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
            First, add a column to store the link (e.g., add a "Company" column
            to store which company each record belongs to).
          </Typography>
        </Box>
      )}

      {/* Existing relationships - only show outgoing from this table */}
      {relationships.filter((rel) => rel.sourceTableId === currentTable.tempId)
        .length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          {relationships
            .filter((rel) => rel.sourceTableId === currentTable.tempId)
            .map((rel) => {
              const info = getRelationshipInfo(rel);
              const tooltipText = `The ${info.sourceTable} table's ${info.sourceColumn} field links to the ${info.targetColumn} field of the ${info.targetTable} table.`;
              return (
                <Box
                  key={rel.tempId}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1.5,
                    borderRadius: "sm",
                    bgcolor: "background.level1",
                  }}
                >
                  <Tooltip title={tooltipText} placement="top" arrow>
                    <Typography level="body-sm" sx={{ cursor: "help" }}>
                      <Box component="span" sx={{ fontWeight: "md" }}>
                        {info.sourceColumn}
                      </Box>
                      {" links to "}
                      <Box component="span" sx={{ fontWeight: "md" }}>
                        {info.targetTable}
                      </Box>
                    </Typography>
                  </Tooltip>
                  <Chip
                    size="sm"
                    variant="soft"
                    color={
                      rel.onDelete === "CASCADE"
                        ? "danger"
                        : rel.onDelete === "SET_NULL"
                          ? "warning"
                          : "neutral"
                    }
                  >
                    {info.onDeleteDisplay}
                  </Chip>
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="danger"
                    onClick={() => onDeleteRelationship(rel.tempId)}
                    sx={{ ml: "auto" }}
                  >
                    <DeleteRoundedIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
        </Box>
      )}

      {/* Add relationship form */}
      {isAdding && (
        <Box
          sx={{
            p: 2,
            borderRadius: "sm",
            border: "1px solid",
            borderColor: "primary.300",
            bgcolor: "background.surface",
          }}
        >
          <Typography level="title-sm" sx={{ mb: 2 }}>
            Connect to Another Table
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl size="sm">
              <FormLabel>Column that stores the link</FormLabel>
              <Select
                value={newRelation.sourceColumnId}
                onChange={(_, value) =>
                  setNewRelation({
                    ...newRelation,
                    sourceColumnId: value || "",
                  })
                }
              >
                {availableColumns.map((col) => (
                  <Option key={col.tempId} value={col.tempId}>
                    {col.displayName || col.name}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ArrowForwardRoundedIcon sx={{ color: "text.tertiary" }} />
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                connects to
              </Typography>
            </Box>

            <FormControl size="sm">
              <FormLabel>Table to connect to</FormLabel>
              <Select
                value={newRelation.targetTableId}
                onChange={(_, value) => handleTargetTableChange(value || "")}
              >
                {otherTables.map((table) => (
                  <Option key={table.tempId} value={table.tempId}>
                    {table.displayName || table.name}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl size="sm">
              <FormLabel>Match by column</FormLabel>
              <Select
                value={newRelation.targetColumnId}
                onChange={(_, value) =>
                  setNewRelation({
                    ...newRelation,
                    targetColumnId: value || "",
                  })
                }
              >
                {targetColumns.map((col) => (
                  <Option key={col.tempId} value={col.tempId}>
                    {col.displayName || col.name}
                    {col.isPrimaryKey && " (ID)"}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl size="sm">
              <FormLabel>On delete</FormLabel>
              <Select
                value={newRelation.onDelete}
                onChange={(_, value) =>
                  setNewRelation({
                    ...newRelation,
                    onDelete: (value as OnDeleteBehavior) || "RESTRICT",
                  })
                }
              >
                <Option value="RESTRICT">
                  Restrict -- block delete if dependents exist
                </Option>
                <Option value="CASCADE">
                  Cascade -- delete dependents automatically
                </Option>
                <Option value="SET_NULL">
                  Set Null -- clear the link in dependents
                </Option>
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                size="sm"
                variant="plain"
                color="neutral"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={
                  !newRelation.sourceColumnId ||
                  !newRelation.targetTableId ||
                  !newRelation.targetColumnId
                }
              >
                Add Relationship
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Card>
  );
}

export default RelationshipBuilder;
